import { Box, Alert, Typography, AlertTitle } from "@mui/material";
import SidebarLayout, { SidebarSection } from "components/common/SidebarLayout";
import React, { useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import {
  PatientHeader,
  PatientInfo,
  Appointments,
  VitalSigns,
  NutritionAndHydration,
  PatientHeaderData,
  PatientInfoData,
  Appointment,
  Activities,
  ReproductiveHealth,
  BodyMeasurements,
  Medications,
} from ".";
import { FunctionalLifestyleResponseDto } from "../../../../libs/cellera-sdk";
import CustomTabs, { TabConfig } from "../../../common/CustomTabs";
import AdditionalNotes from "../AdditionalNotes";
import DietAndNutrition from "../DietAndNutrition";
import EnvironmentToxins from "../EnvironmentToxins";
import FamilyHistory from "../FamilyHistory";
import FunctionalLifestyleAssessment from "../FunctionalLifestyleAssessment";
import GoalsOfTreatment from "../GoalsOfTreatment";
import PastMedicalHistory from "../PastMedicalHistory";
import PatientBiomarkers from "../PatientBiomarkers";
import PatientGenetics from "../PatientGenetics";
import PresentingConcerns from "../PresentingConcerns";
import SocialHistory from "../SocialHistory";
import { DailyWellBeingScore } from "./DailyWellBeingScore";
import Sleep from "./Sleep";

const DataNotAvailableAlert: React.FC = () => {
  const { colors } = useCelleraTheme();

  return (
    <Alert
      severity="info"
      sx={{
        borderRadius: 1,
        backgroundColor: colors.primary.light,
        border: `1px solid ${colors.primary.main}`,
        "& .MuiAlert-icon": {
          color: colors.primary.main,
        },
      }}
    >
      <AlertTitle
        sx={{
          color: colors.primary.main,
          fontWeight: 600,
        }}
      >
        Information
      </AlertTitle>
      <Typography variant="body1">
        Patient data is not yet available. Please wait for the patient to
        confirm sharing.
      </Typography>
    </Alert>
  );
};

export interface PatientDetailData {
  id: string;
  name: string;
  patientId: string;
  age: number;
  email?: string;
  gender: string;
  avatar?: string;
  status?: string;
  personalInfo: {
    dateOfBirth: string;
    phone: string;
    address: string;
    emergencyContact: string;
  };
  medicalInfo: {
    bloodType: string;
    allergies: string;
    conditions: string;
    insuranceStatus: string;
  };
  billingInfo: {
    billingStatus: string;
    payment: string;
    paymentType: string;
    paymentProfile: string;
  };
  appointments: Appointment[];
  functionalLifestyle?: FunctionalLifestyleResponseDto;
}

export const PATIENT_SECTIONS = {
  overview: {
    id: "overview",
    label: "Overview",
    description: "General patient information",
  },
  functionalLifestyle: {
    id: "functional-lifestyle",
    label: "Functional & Lifestyle Assessment",
    description: "Health metrics and lifestyle data",
  },
  goalsOfTreatment: {
    id: "goals-of-treatment",
    label: "Goals of Treatment",
    description: "Goals of treatment data",
  },
  presentingConcerns: {
    id: "presenting-concerns",
    label: "Presenting Concerns",
    description: "Current health concerns",
  },
  medicalHistory: {
    id: "medical-history",
    label: "Medical History",
    description: "Historical medical data",
  },
  environmentToxins: {
    id: "environment-toxins",
    label: "Environment Toxins",
    description: "Environmental exposure data",
  },
  medications: {
    id: "medications",
    label: "Medications",
    description: "Current and past medications",
  },
  dietNutrition: {
    id: "diet-nutrition",
    label: "Diet & Nutrition",
    description: "Diet and Nutrition",
  },
  generalInformation: {
    id: "general-information",
    label: "General Information",
    description: "General Information",
  },
  socialHistory: {
    id: "social-history",
    label: "Social History",
    description: "Social History",
  },
  familyHistory: {
    id: "family-history",
    label: "Family History",
    description: "Family History",
  },
  additionalNotes: {
    id: "additional-notes",
    label: "Additional Notes",
    description: "Additional Notes",
  },
};

interface PatientDetailProps {
  patient: PatientDetailData;
  hasRequestedSuccessfullyBefore: boolean;
  onViewAllAppointments: () => void;
  onViewVitalHistory: () => void;
  onViewActivities: () => void;
  onViewBodyMeasurements: () => void;
  onViewNutritionHistory?: () => void;
  onViewSleepHistory?: () => void;
  onCancelRequest?: () => void;
  onRequest?: () => void;
}

const PatientDetail: React.FC<PatientDetailProps> = ({
  patient,
  hasRequestedSuccessfullyBefore,
  onViewAllAppointments,
  onViewVitalHistory,
  onViewActivities,
  onViewBodyMeasurements,
  onViewNutritionHistory,
  onViewSleepHistory,
  onCancelRequest,
  onRequest,
}) => {
  const [selectedHistorySection, setSelectedHistorySection] =
    useState<string>("medical-history");

  const [
    selectedMedicalAssessmentsSection,
    setSelectedMedicalAssessmentsSection,
  ] = useState<string>(PATIENT_SECTIONS.functionalLifestyle.id);

  const medicalAssessmentsSections: SidebarSection[] = [
    PATIENT_SECTIONS.functionalLifestyle,
    PATIENT_SECTIONS.presentingConcerns,
    PATIENT_SECTIONS.goalsOfTreatment,
  ];

  // History & Lifestyle sections
  const historySections: SidebarSection[] = [
    {
      id: "medical-history",
      label: "Past Medical History",
      description: "Historical medical data",
    },
    {
      id: "medications",
      label: "Current Medications & Supplements",
      description: "Current and past medications",
    },
    {
      id: "family-history",
      label: "Family History",
      description: "Family medical history",
    },
    {
      id: "social-history",
      label: "Social History",
      description: "Social and lifestyle factors",
    },
    {
      id: "diet-nutrition",
      label: "Diet & Nutrition",
      description: "Dietary habits and nutrition",
    },
    {
      id: "environment-toxins",
      label: "Environment & Toxins",
      description: "Environmental exposure data",
    },
    {
      id: "additional-notes",
      label: "Additional Notes",
      description: "Additional medical notes",
    },
  ];

  const handleHistorySectionChange = (sectionId: string) => {
    setSelectedHistorySection(sectionId);
  };

  const handleMedicalAssessmentsSectionChange = (sectionId: string) => {
    setSelectedMedicalAssessmentsSection(sectionId);
  };

  // Transform data for PatientHeader
  const patientHeaderData: PatientHeaderData = {
    patientId: patient.patientId,
    avatar: patient.avatar,
    name: patient.name,
    email: patient.email,
    birthday: patient.personalInfo.dateOfBirth,
    phone: patient.personalInfo.phone,
    status: patient.status,
  };

  // Transform data for PatientInfo
  const patientInfoData: PatientInfoData = {
    personalInfo: patient.personalInfo,
    medicalInfo: patient.medicalInfo,
    billingInfo: patient.billingInfo,
  };

  const renderOverviewContent = () => {
    return (
      <>
        {/* Patient Information Cards */}
        <PatientInfo patientInfo={patientInfoData} />

        {/* Appointments Section */}
        <Appointments
          appointments={patient.appointments}
          onViewAll={onViewAllAppointments}
        />

        {/* Vital Signs Section */}
        <VitalSigns patientId={patient.id} onViewHistory={onViewVitalHistory} />

        {/* Activities Section */}
        <Activities patientId={patient.id} onViewHistory={onViewActivities} />

        {/* Sleep Section */}
        <Sleep
          patientId={patient.id}
          onViewHistory={onViewSleepHistory || onViewVitalHistory}
        />

        {/* Nutrition and Hydration Section */}
        <NutritionAndHydration
          patientId={patient.id}
          onViewHistory={onViewNutritionHistory || onViewVitalHistory}
        />

        {/* Daily Well Being Score Section */}
        <DailyWellBeingScore patientId={patient.id} />

        {/* Body Measurements Section */}
        <BodyMeasurements
          patientId={patient.id}
          onViewHistory={onViewBodyMeasurements}
        />

        {/* Reproductive Health Section */}
        <ReproductiveHealth patientId={patient.id} />
      </>
    );
  };

  const renderHistorySectionContent = () => {
    switch (selectedHistorySection) {
      case "medical-history":
        return <PastMedicalHistory patientId={patient.id} />;
      case "medications":
        return <Medications patientId={patient.id} />;
      case "family-history":
        return <FamilyHistory patientId={patient.id} />;
      case "social-history":
        return <SocialHistory patientId={patient.id} />;
      case "diet-nutrition":
        return <DietAndNutrition patientId={patient.id} />;
      case "environment-toxins":
        return <EnvironmentToxins patientId={patient.id} />;
      case "additional-notes":
        return <AdditionalNotes patientId={patient.id} />;
      default:
        return null;
    }
  };

  const renderMedicalAssessmentsContent = () => {
    switch (selectedMedicalAssessmentsSection) {
      case PATIENT_SECTIONS.functionalLifestyle.id:
        return <FunctionalLifestyleAssessment patientId={patient.id} />;
      case PATIENT_SECTIONS.presentingConcerns.id:
        return <PresentingConcerns patientId={patient.id} />;
      case PATIENT_SECTIONS.goalsOfTreatment.id:
        return <GoalsOfTreatment patientId={patient.id} />;
      default:
        return null;
    }
  };

  // Define tabs configuration
  const tabs: TabConfig[] = [
    {
      label: "Patient Details",
      content: !hasRequestedSuccessfullyBefore ? (
        <DataNotAvailableAlert />
      ) : (
        renderOverviewContent()
      ),
    },
    {
      label: "Medical Assessments",
      content: !hasRequestedSuccessfullyBefore ? (
        <DataNotAvailableAlert />
      ) : (
        <SidebarLayout
          sections={medicalAssessmentsSections}
          selectedSectionId={selectedMedicalAssessmentsSection}
          onSectionChange={handleMedicalAssessmentsSectionChange}
        >
          {renderMedicalAssessmentsContent()}
        </SidebarLayout>
      ),
    },
    {
      label: "History & Lifestyle",
      content: !hasRequestedSuccessfullyBefore ? (
        <DataNotAvailableAlert />
      ) : (
        <SidebarLayout
          sections={historySections}
          selectedSectionId={selectedHistorySection}
          onSectionChange={handleHistorySectionChange}
        >
          {renderHistorySectionContent()}
        </SidebarLayout>
      ),
    },
    {
      label: "Patient Genetics",
      content: !hasRequestedSuccessfullyBefore ? (
        <DataNotAvailableAlert />
      ) : (
        <PatientGenetics patientId={patient.id} />
      ),
    },
    {
      label: "Patient Biomarkers",
      content: !hasRequestedSuccessfullyBefore ? (
        <DataNotAvailableAlert />
      ) : (
        <PatientBiomarkers patientId={patient.id} />
      ),
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        height: "calc(100vh - 170px)",
        maxHeight: "calc(100vh - 170px)",
      }}
    >
      {/* Patient Header */}
      <PatientHeader
        patient={patientHeaderData}
        onCancelRequest={onCancelRequest}
        onRequest={onRequest}
      />

      {/* Tabs */}
      <Box sx={{ mt: 3, height: "calc(100% - 100px)" }}>
        <CustomTabs tabs={tabs} defaultTab={0} />
      </Box>
    </Box>
  );
};

export default PatientDetail;
